// Client-side image capture service
// This service handles capturing images from the client-side SVG and uploading them to S3

import { API_ENDPOINTS } from '../config';

class ClientImageCaptureService {
  constructor() {
    this.captureFunctions = new Map();
  }

  // Register a capture function for a specific component
  registerCaptureFunction(componentId, captureFunction) {
    console.log(`📝 [CLIENT-CAPTURE] Registering capture function for component: ${componentId}`);
    this.captureFunctions.set(componentId, captureFunction);
  }

  // Unregister a capture function
  unregisterCaptureFunction(componentId) {
    console.log(`🗑️ [CLIENT-CAPTURE] Unregistering capture function for component: ${componentId}`);
    this.captureFunctions.delete(componentId);
  }

  // Capture image from a specific component
  async captureComponentImage(componentId) {
    console.log(`🎯 [CLIENT-CAPTURE] captureComponentImage called for: ${componentId}`);
    console.log(`🎯 [CLIENT-CAPTURE] Registered components:`, this.getRegisteredComponents());
    
    const captureFunction = this.captureFunctions.get(componentId);
    if (!captureFunction) {
      console.warn(`⚠️ [CLIENT-CAPTURE] No capture function registered for component: ${componentId}`);
      console.warn(`⚠️ [CLIENT-CAPTURE] Available components:`, this.getRegisteredComponents());
      return null;
    }

    try {
      console.log(`🔄 [CLIENT-CAPTURE] Capturing image for component: ${componentId}`);
      const blob = await captureFunction();
      
      if (!blob) {
        console.warn(`⚠️ [CLIENT-CAPTURE] No image blob returned for component: ${componentId}`);
        return null;
      }

      console.log(`✅ [CLIENT-CAPTURE] Image captured successfully for component: ${componentId}, blob size:`, blob.size);
      return blob;
    } catch (error) {
      console.error(`❌ [CLIENT-CAPTURE] Error capturing image for component ${componentId}:`, error);
      return null;
    }
  }

  // Upload captured image to S3
  async uploadImageToS3(blob, sku, timestamp) {
    try {
      console.log(`🔄 [CLIENT-CAPTURE] Uploading image to S3...`);
      
      // Convert blob to base64
      const base64 = await this.blobToBase64(blob);
      
      const response = await fetch(API_ENDPOINTS.UPLOAD_DIAGRAM, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sku,
          timestamp,
          imageData: base64,
          contentType: 'image/png'
        })
      });

      if (response.ok) {
        const data = await response.json();
        
        // Handle fallback response when EC2 is unavailable
        if (data.fallback && data.dataUrl) {
          console.log(`✅ [CLIENT-CAPTURE] Image processed via fallback (EC2 unavailable):`, data.url);
          console.log(`📝 [CLIENT-CAPTURE] Note:`, data.note);
          return data.url; // This is the data URL
        }
        
        console.log(`✅ [CLIENT-CAPTURE] Image uploaded to S3:`, data.url);
        return data.url;
      } else {
        const error = await response.text();
        console.error(`❌ [CLIENT-CAPTURE] Failed to upload image to S3:`, error);
        return null;
      }
    } catch (error) {
      console.error(`❌ [CLIENT-CAPTURE] Error uploading image to S3:`, error);
      return null;
    }
  }

  // Convert blob to base64
  blobToBase64(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        // Return the full data URL for the Vercel proxy
        resolve(reader.result);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  // Capture and upload image for a component
  async captureAndUploadImage(componentId, sku, timestamp) {
    const blob = await this.captureComponentImage(componentId);
    if (!blob) {
      return null;
    }

    const s3Url = await this.uploadImageToS3(blob, sku, timestamp);
    return s3Url;
  }

  // Get all registered component IDs
  getRegisteredComponents() {
    return Array.from(this.captureFunctions.keys());
  }

  // Check if a component is registered
  isComponentRegistered(componentId) {
    return this.captureFunctions.has(componentId);
  }
}

// Create singleton instance
const clientImageCaptureService = new ClientImageCaptureService();

export default clientImageCaptureService;
